import "./Select.scss";
import { ChevronArrowRight, Close } from "../../../assets/icons";
import React, { useState, useEffect, useRef } from "react";
import NoDataIcon from "../../../assets/icons/NoDataFound";
import { debounce, handleSearch } from "../../../utilities/commonUtilities";
import { useAppSelector } from "../../../store/hooks/StoreHooks";

interface SelectProps {
  label?: string;
  options: { [key: string]: any }[];
  onChange: (
    selectedOption: { [key: string]: any },
    name?: string | undefined
  ) => void;
  selectedValue?: string | number;
  fontSize?: string;
  width?: string;
  className?: string;
  padding?: string;
  optionFontSize?: string;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  showCloseIcon?: boolean;
  onClear?: () => void;
  isNotFound?: any;
  isSearch?: boolean;
  onChangeSearch?: any;
  styles?: { [key: string]: any };
  required?: boolean;
  isWrap?: boolean;
  onCloseDropdown?: any;
  targetName?: string;
  parentRef?: React.RefObject<HTMLElement>
  dropdownDirection?: "down" | "up";
}

const Select: React.FC<SelectProps> = ({
  label,
  options,
  onChange,
  selectedValue = "",
  fontSize = "14px",
  width,
  className,
  padding,
  optionFontSize = "14px",
  placeholder = "Select an option",
  disabled = false,
  name,
  showCloseIcon = false,
  onClear,
  isNotFound,
  isSearch = false,
  onChangeSearch,
  styles,
  required,
  isWrap = false,
  onCloseDropdown,
  targetName,
  parentRef,
  dropdownDirection,
}) => {

  const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
  const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);

  const [selectedOption, setSelectedOption] = useState<{
    [key: string]: any;
  } | null>();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const [rect, setRect] = useState({ top: 0, left: 0 });

useEffect(() => {
  setFilteredOptions(options ?? []);
}, [options])

  useEffect(() => {
    updateRect();
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFilteredOptions(options);
        if (name === targetName) {
          onCloseDropdown && onCloseDropdown();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, name]);

  const updateRect = () => {
    if (dropdownContainerRef.current) {
      const { left, top, bottom } = dropdownContainerRef.current.getBoundingClientRect();
      setRect({ left, top: bottom });
    }
  };

  useEffect(() => {
    if (parentRef?.current) {
      parentRef.current.addEventListener("scroll", () => setIsOpen(false));
    }
    return () => {
      window.removeEventListener("scroll", () => setIsOpen(false));
    };
  }, [parentRef])

  useEffect(() => {
    const observer = new ResizeObserver(updateRect);
    if (dropdownContainerRef.current) observer.observe(dropdownContainerRef.current);
    window.addEventListener("scroll", updateRect);
    updateRect();


    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateRect);
    };
  }, []);

  useEffect(() => {
    if (selectedValue !== "" && options?.length > 0) {
      const newSelectedOption =
        (options.length > 0 &&
          options.find(
            (option) =>
              option.value == selectedValue || option.label == selectedValue || option.id == selectedValue || option.name == selectedValue
          )) ||
        null;
      setSelectedOption(newSelectedOption);
    } else {
      setSelectedOption(null);
    }
  }, [selectedValue, options]);

  const handleSelectOption = (
    option: { [key: string]: any },
    name?: string
  ) => {
    if (!disabled) {
      setSelectedOption(option);
      setIsOpen(false);
      onChange(option, name);
      setFilteredOptions(options);
    }
  };

  const handleClearSelection = () => {
    setSelectedOption(null);
    onClear && onClear();
  };

  const handleLocalSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sourceOptions = options ?? [];
    let searchValue = handleSearch(e.target.value, sourceOptions, "label");
    setFilteredOptions(searchValue);
  }

  return (
    <>
      {
        <>
          <div
            className={`relative ${className}`}
            ref={dropdownRef}
            style={{ width }}
            data-name={name}
          >
            {label && (
              <label className="block text-[14px] text-neutral-25 mb-1">
                {label}
                {required && <span className="text-error-400 ml-1">*</span>}
              </label>
            )}
            <div ref={dropdownContainerRef} onClick={() => (!disabled && ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated && !projectReadOnly))) && setIsOpen(!isOpen)}
              className={`relative w-full border rounded-[8px] bg-transparent ${((customerEnabled && projectReadOnly) || (customerEnabled && !customerCreated)) ? "cursor-not-allowed" : "cursor-pointer"} ${padding} ${disabled ? "opacity-50 cursor-not-allowed" : ""
                } ${isNotFound ? "border-error-700" : "border-darkcolor-400"}`}
            >
              <div className="flex items-center" >
                <span
                  className={`block ${isWrap ? "break-all pr-[2.25rem] " : 'truncate'} text-neutral-25 ${((customerEnabled && !customerCreated) || projectReadOnly) && "text-neutral-400 cursor-not-allowed"}`}
                  style={{ fontSize }}
                >
                  {selectedOption?.value === "-2" ? placeholder : selectedOption?.label || placeholder}
                </span>

                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                  {showCloseIcon && selectedValue && ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated)) && (
                    <Close
                      className="cursor-pointer"
                      onClick={handleClearSelection}
                      size={18}
                    />
                  )}
                  <ChevronArrowRight
                    className={`transform transition-transform cursor-pointer  ${isOpen ? "-rotate-90" : "rotate-90"}`}
                    size={20}
                    color={`${((customerEnabled && !customerCreated) || projectReadOnly) ? "#A3A3A3" : "#FFFFFF"}`}
                    onClick={() => (!disabled && ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated))) && setIsOpen(!isOpen)}
                  />
                </span>
              </div>
            </div>

            {isOpen && (
              <div
                className="border-[1px] border-darkcolor-400 origin-top-right mt-[3px] w-full rounded-[8px] bg-darkcolor-700 max-h-[200px] overflow-y-auto fixed"
                style={{
                  zIndex: 99999,
                  width: dropdownRef?.current?.offsetWidth,
                  left: rect.left,
                  ...(dropdownDirection === "up"
                    ? { bottom: window.innerHeight - rect.top + 50 }
                    : { top: rect.top })
                }}
              >
                {
                  options?.length > 0 && (
                    <div className="p-[10px] bg-darkcolor-800">
                      <input
                        onChange={(e) => onChangeSearch ? debounce(onChangeSearch(e), 300) : handleLocalSearch(e)}
                        type="text"
                        name={"search"}
                        disabled={customerEnabled || projectReadOnly}
                        className={`flex px-[12px] py-[10px] rounded-[8px] border-[1px] border-darkcolor-400 w-full bg-transparent text-neutral-25 focus:outline-none text-[16px]`}
                      />
                    </div>
                  )
                }
                {filteredOptions?.length > 0 ? (
                  filteredOptions?.map((option, index) => {
                    return (
                      <div
                        key={option.value ?? option.id}
                        className={`text-[16px] p-[10px] hover:bg-darkcolor-600  cursor-pointer  flex justify-between bg-darkcolor-800 ${index === 0 && "rounded-t-[8px]"
                          } ${index === options.length - 1 ? "rounded-b-[8px]" : ""} ${option?.isHighlight && (index === options.length - 1 || index === 0) ? "bg-primary-800 rounded-b-[0px] rounded-t-[0px]" : option?.isHighlight ? "bg-primary-800" : ""}`}
                        onClick={() => handleSelectOption(option, name)}
                        style={{ zIndex: 999999 }}
                      >
                        <p
                          className={`ml-[8px] text-neutral-25 font-Mulish ${selectedOption?.label === option?.label ? "text-primary-400" : ""}`}
                          style={{ fontSize: optionFontSize }}
                        >
                          {option?.label || option?.name}
                        </p>
                      </div>
                    )
                  }
                  )
                ) : (
                  <div className="flex flex-col justify-center items-center p-[10px]">
                    <NoDataIcon size={60} />
                    <p className="text-neutral-25 text-[14px] mt-[5px]">
                      No Data Found
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      }
    </>

  );
};

export default Select;
